import { useCallback, useEffect, useState } from 'react';
import { crm, type CustomerRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';

/**
 * Kunde entfernen — mit dem Ausweg, den das Backend vorgibt.
 *
 * `demote` schlägt fehl, solange Projekte an der Firma hängen. Das ist kein
 * Fehler, den man wegklickt: der Dialog nennt den Grund und bietet „ehemalig“
 * als das an, was tatsächlich gemeint ist — die Historie bleibt, nur als
 * aktiver Kunde zählt die Firma nicht mehr.
 */
export function DemoteCustomerDialog({
  customer,
  onClose,
  onDone,
}: {
  customer: CustomerRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [blockiert, setBlockiert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const entfernen = useCallback(async () => {
    setBusy(true);
    setFehler(null);
    try {
      const ergebnis = await crm.customers.demote(customer.id);
      if (ergebnis.removed) {
        onDone();
        return;
      }
      if (ergebnis.reason === 'has_projects') {
        setBlockiert(true);
      } else {
        setFehler('Diesen Kunden gibt es nicht mehr.');
      }
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Entfernen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }, [customer.id, onDone]);

  const alsEhemaligMarkieren = useCallback(async () => {
    setBusy(true);
    setFehler(null);
    try {
      await crm.customers.update(customer.id, { status: 'ehemalig' });
      onDone();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Aktualisieren fehlgeschlagen');
      setBusy(false);
    }
  }, [customer.id, onDone]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-md space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold">{customer.organizationName} kein Kunde mehr?</h2>

        {blockiert ? (
          <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
            Daran hängen Projekte — der Kundenstatus lässt sich deshalb nicht
            löschen. Passender ist „ehemalig“: die Historie bleibt erhalten, nur
            als aktiver Kunde zählt die Firma nicht mehr.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Ohne Projekte verschwindet der Kundenstatus vollständig; über „Zum
            Kunden befördern“ in der Firmenakte lässt er sich jederzeit neu
            setzen. Hängen Projekte daran, schlägt der Dialog stattdessen
            „ehemalig“ vor.
          </p>
        )}

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          {blockiert ? (
            <Button onClick={() => void alsEhemaligMarkieren()} disabled={busy}>
              {busy ? 'Speichere …' : 'Als „ehemalig“ markieren'}
            </Button>
          ) : (
            <Button variant="danger" onClick={() => void entfernen()} disabled={busy}>
              {busy ? 'Entferne …' : 'Kundenstatus entfernen'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
